/**
 * useGetHub - Fetch Hub configuration by slug
 *
 * Returns the full Hub configuration for rendering the brand experience hub.
 * Public endpoint - returns only ACTIVE hubs.
 * Note: API endpoint remains at /hubs for backwards compatibility.
 */
import { useQuery, keepPreviousData } from "@tanstack/react-query";
import { useSelector } from "react-redux";
import { IStore } from "@/redux/defaultStore";
import getConfig from "@/utils/getConfig";
import { Token, DropsApi } from "@devour/client";

/*
 * RE-EXPORTED FROM THE GENERATED CLIENT — never mirrored here.
 *
 * This was a hand-written interface duplicating the API's own
 * `FeaturedStreamResponse` field for field: the pattern the repo bans
 * outright, because a local mirror keeps compiling while the schema moves and
 * the drift only surfaces at runtime.
 *
 * It surfaced exactly that way. `FeaturedStream.platform` became OPTIONAL — a
 * roster member is not required to have a channel, and platform is a property
 * of a channel — but this copy still declared
 * `platform: "TWITCH" | "YOUTUBE" | "KICK"` as required. Every consumer
 * type-checked against the stale copy, so `StreamingSection.tsx`'s six
 * unguarded `stream.platform.toLowerCase()` calls compiled clean and would
 * have thrown `TypeError: Cannot read properties of undefined` on the first
 * bare member, taking the whole streaming section down. It also silently
 * neutered the `NonNullable<>` in `widgets/shared.tsx`, which was unwrapping
 * an optionality this copy never had.
 *
 * Sourcing it from `@devour/client` means the generator is the single author
 * of this shape and the compiler sees schema changes the moment they land.
 */
import type { FeaturedStreamResponse, SurfaceRulesSection } from "@devour/client";

/**
 * Which collection to fetch from:
 * - all surfaces → /drops/:slug — Hub is retired
 * - "drop" → /drops/slug/:slug (creator/community pages, used by /drop-in/:slug)
 *
 * Drop response is structurally compatible with HubResponse for HubPage rendering;
 * the only adapter we apply is mapping `dropType` → `hubType` so HubPage's
 * `hubType === "CREATOR"` branches (creator streaming section, etc.) still fire.
 */

/**
 * Response types matching backend HubResponse
 * Uses Challenge design system types
 */

export interface SurfaceThemeColors {
    primary?: string;
    primaryDark?: string;
    secondary?: string;
    secondaryDark?: string;
    accent?: string;
    background?: string;
    cardBackground?: string;
    textPrimary?: string;
    textSecondary?: string;
    success?: string;
    error?: string;
}

export interface SurfaceTypography {
    headingFont?: string;
    bodyFont?: string;
    accentFont?: string;
    displayFont?: string;
}

export interface SurfaceThemeConfigResponse {
    preset?: SurfaceThemePreset;
    surfaceGlow?: string;
    borderSubtle?: string;
    gradientStart?: string;
    gradientEnd?: string;
    navBackground?: string;
    heroOverlay?: string;
    borderRadius?: "none" | "sm" | "md" | "lg" | "pill";
    cardStyle?: "flat" | "elevated" | "glass" | "bordered";
    shadowIntensity?: "none" | "subtle" | "medium" | "dramatic";
    spacing?: "compact" | "comfortable" | "generous";
    animationLevel?: "none" | "subtle" | "full";
    heroParallax?: boolean;
    stickyNav?: boolean;
    headingWeight?: number;
    headingLineHeight?: number;
    headingLetterSpacing?: number;
    bodySize?: "sm" | "md" | "lg";
    headingTransform?: "none" | "uppercase" | "capitalize";
    backgroundImageUrl?: string;
}

// Responsive typography (matches Challenge design system)
export interface ResponsiveTypography {
    font?: string;
    desktopSize?: string;
    tabletSize?: string;
    mobileSize?: string;
}

// HeroSection - matches PageHeroSectionResponse
export interface HubHeroSection {
    heroType?: "IMAGE" | "VIDEO";
    heroImageUrl?: string;
    /** External URL for hero image - auto-populated from social banner for creator hubs */
    heroImageExternalUrl?: string;
    videoUrl?: string;
    videoPosterImageUrl?: string;
    title?: string;
    description?: string;
    ctaText?: string;
    ctaBackgroundColor?: string;
    ctaTextColor?: string;
    showGradientOverlay?: boolean;
    gradientStartColor?: string;
    gradientEndColor?: string;
    gradientOpacity?: number;
    gradientCoverage?: number;
    gradientDirection?: string;
    heroLayout?: "default" | "split";
    splitWidgetBlockId?: string;
    titleTypography?: ResponsiveTypography;
    subtitleTypography?: ResponsiveTypography;
    descriptionTypography?: ResponsiveTypography;
}

// NavbarConfig - matches PageNavbarConfigResponse
export interface HubNavbarConfig {
    backgroundColor?: string;
    textColor?: string;
    iconColor?: string;
    borderColor?: string;
    borderVisible?: boolean;
    customAvatarImageUrl?: string;
    showTimer?: boolean;
    timerPosition?: "left" | "center" | "right";
    timerColor?: string;
    countdownTargetDate?: number;
    accentBarGradient?: string;
    /** On mobile, pin nav to top (default) or bottom of the screen */
    mobileNavStyle?: "top" | "bottom";
}

// SEO - matches PageSeoResponse
export interface HubSeo {
    title?: string;
    description?: string;
    socialShareImageUrl?: string;
    faviconUrl?: string;
    canonicalUrl?: string;
}

export type SurfaceSectionType =
    | "GAMES"
    | "MISSIONS"
    | "TOURNAMENTS"
    | "LEAGUES"
    | "LADDERS"
    | "LEADERBOARDS"
    | "GUILDS"
    | "CHALLENGES"
    | "CAMPAIGNS"
    | "EXCLUSIVES"
    | "STREAMING"
    | "INFO"
    | "CUSTOM"
    /* The title's own Steam announcements. DropSection-only today. */
    | "NEWS"
    /* One specifically assigned engagement. DropSection-only today. */
    | "ENGAGEMENT"
    /*
     * The rosters this creator is on — the reverse of STREAMING.
     *
     * STREAMING answers "who is on this Drop's roster"; this answers "whose
     * rosters is this Drop on". Derived entirely from existing membership, so
     * it needs no configuration beyond being placed in the section order.
     * DropSection-only, like NEWS and ENGAGEMENT.
     */
    | "OFFICIAL_FOR";

export interface HubSection {
    _id: string;
    type: SurfaceSectionType;
    label: string;
    icon: string;
    order: number;
    enabled: boolean;
    challengeId?: string;
    competitionFilter?: Record<string, unknown>;
    guildId?: string;
    customContent?: string;
}

export type RetailVerifyMethod = "RECEIPT" | "CODE" | "QR" | "APP_LINK";

export interface HubRetailCampaign {
    _id: string;
    retailer: string;
    verifyMethod: RetailVerifyMethod;
    questId?: string;
    logoImageId?: string;
    logoImageUrl?: string;
    description?: string;
    rewardDescription?: string;
    enabled: boolean;
    order: number;
}
export type { FeaturedStreamResponse };

export interface StreamingSettings {
    enabled: boolean;
    allowParticipantStreams: boolean;
    discoverableInLiveHub: boolean;
    requireApproval: boolean;
    showViewerCounts: boolean;
    maxFeaturedStreams: number;
    featuredStreams: FeaturedStreamResponse[];
}

/**
 * Derived game from competitions or social profiles
 * Computed at runtime, not stored in database
 */
export interface DerivedGameResponse {
    id: string;
    name: string;
    thumbnailUrl?: string;
    source: "competition" | "social";
    sourcePlatform?: string;
}

/**
 * Point system configuration for hub
 * Determines whether to show custom points or DevourPlay XP
 */
export interface SurfacePointSystemConfigResponse {
    useCustomPoints: boolean;
    pointsName?: string;
    pointsIconUrl?: string;
}

/*
 * ============================================
 * Content Block Types
 * ============================================
 */

export type SurfaceContentBlockType = "HTML" | "PROMO" | "TEXT_IMAGE" | "REWARDS" | "PRODUCTS" | "COUNTDOWN" | "SPONSORS" | "LIVE_BRACKET" | "STEPS" | "PRIZES" | "FAQ" | "LEADERBOARD" | "CTA_BANNER" | "VIDEO" | "RICH_TEXT" | "DIVIDER" | "PLAYABLE_AD" | "BRAND_CHALLENGE" | "QUEST_SPONSORSHIP" | "GUILD_SPONSORSHIP" | "COMPETITION" | "QUEST" | "STREAMING" | "NOTIFICATION_SIGNUP" | "POLL" | "IMAGE_GALLERY" | "STATS" | "SOCIAL_EMBED" | "CONTAINER" | "COLUMNS" | "SCROLL_NAV" | "STORE_LOCATOR" | "MY_POSITION" | "ACHIEVEMENTS" | "ONBOARDING" | "EARNING" | "CODE_ENTRY" | "GAMIFICATION" | "LOOTBOX" | "STREAM_ZONE" | "CHALLENGE_ZONE";

export interface SurfacePromoItemResponse {
    _id?: string;
    imageUrl?: string;
    title?: string;
    description?: string;
    url?: string;
}

export interface SurfaceRewardTierResponse {
    _id?: string;
    name: string;
    description?: string;
    imageId?: string;
    imageUrl?: string;
    requiredLevel: number;
    requiredPoints?: number;
    bonusText?: string;
    category: "IN_GAME" | "MERCH" | "EXCLUSIVE" | "XP_BOOST";
}

export interface SurfaceHowToPlayStepResponse {
    _id?: string;
    title: string;
    description: string;
    icon: string;
}

export interface SurfaceRewardsConfigResponse {
    sectionTitle?: string;
    rewards?: SurfaceRewardTierResponse[];
    heroImageUrl?: string;
    howToPlaySteps?: SurfaceHowToPlayStepResponse[];
}

export interface SurfaceProductResponse {
    _id?: string;
    name?: string;
    description?: string;
    imageUrl?: string;
    price?: string;
    productUrl?: string;
    /** The buy CTA's label; "Shop" when unset. */
    ctaText?: string;
}

export interface SurfaceRetailerResponse {
    _id?: string;
    name?: string;
    logoImageId?: string;
    logoImageUrl?: string;
    logoUrl?: string;
    productUrl?: string;
    url?: string;
    type?: "online" | "in-store" | "both";
    exclusive?: string;
}

export interface SurfaceProductsConfigResponse {
    sectionTitle?: string;
    subtitle?: string;
    products?: SurfaceProductResponse[];
    retailers?: SurfaceRetailerResponse[];
    backgroundImageUrl?: string;
}

export interface SurfaceGalleryImageResponse {
    _id?: string;
    imageId?: string;
    imageUrl?: string;
    caption?: string;
    alt?: string;
}

export interface SurfaceImageGalleryConfigResponse {
    images?: SurfaceGalleryImageResponse[];
    layout?: "carousel" | "grid";
    columns?: number;
}

export interface SurfaceStatItemResponse {
    _id?: string;
    value: string;
    label: string;
    prefix?: string;
    suffix?: string;
}

export interface SurfaceStatsConfigResponse {
    items?: SurfaceStatItemResponse[];
    layout?: "row" | "grid";
    columns?: number;
}

export interface SurfaceSocialEmbedConfigResponse {
    platform: "twitter" | "tiktok" | "youtube" | "instagram";
    embedUrl?: string;
}

export interface SurfaceStreamingBlockConfigResponse {
    showChat?: boolean;
    showViewerCount?: boolean;
    layout?: "theater" | "compact" | "featured";
    maxStreams?: number;
}

export interface SurfaceNotificationSignupConfigResponse {
    heading?: string;
    description?: string;
    buttonText?: string;
    channels?: string[];
    successMessage?: string;
}

export interface SurfacePollOptionResponse {
    _id?: string;
    label: string;
    imageUrl?: string;
}

export interface SurfacePollConfigResponse {
    question: string;
    options?: SurfacePollOptionResponse[];
    allowMultiple?: boolean;
    showResults?: boolean;
}

export type SponsorTier = "PRESENTING" | "SUPPORTING" | "COMMUNITY";

export interface SurfaceSponsorItemResponse {
    _id?: string;
    name: string;
    tier: SponsorTier;
    logoImageUrl?: string;
    url?: string;
}

/**
 * Per-block visual design controls applied by ContentBlockRenderer (@/widgets/ContentBlockRenderer).
 * backgroundOverlayOpacity: 0–100 integer (divided by 100 for CSS opacity at render time).
 */
export interface SurfaceBlockDesign {
    backgroundColor?: string;
    backgroundImageId?: string;
    backgroundImageUrl?: string;
    backgroundGradient?: string;
    backgroundOverlayOpacity?: number;
    padding?: "none" | "sm" | "md" | "lg" | "xl";
    fullBleed?: boolean;
    textAlignment?: "left" | "center" | "right";
    borderTop?: boolean;
    borderBottom?: boolean;
    roundedCorners?: boolean;
    animateOnScroll?: "none" | "fade-up" | "fade-in" | "slide-left" | "slide-right";
}

export interface SurfaceStepItemResponse {
    _id: string;
    title: string;
    description: string;
    iconType?: "number" | "icon" | "image";
    iconName?: string;
    imageId?: string;
    imageUrl?: string;
    value?: string;
}

export interface SurfaceStepsConfigResponse {
    steps: SurfaceStepItemResponse[];
    layout?: "horizontal" | "vertical" | "alternating";
    connectorStyle?: "line" | "arrow" | "dots" | "none";
    accentColor?: string;
}

export type SurfacePrizeTier = "GRAND" | "FIRST" | "SECOND" | "THIRD" | "PARTICIPATION";

export interface SurfacePrizeItemResponse {
    _id: string;
    tier: SurfacePrizeTier;
    title: string;
    description?: string;
    value?: string;
    imageId?: string;
    imageUrl?: string;
    quantity?: number;
}

export interface SurfacePrizesConfigResponse {
    prizes: SurfacePrizeItemResponse[];
    layout?: "carousel" | "grid" | "featured";
    showValues?: boolean;
}

export interface SurfaceFaqItemResponse {
    _id: string;
    question: string;
    answer: string;
    defaultOpen?: boolean;
}

export interface SurfaceFaqConfigResponse {
    items: SurfaceFaqItemResponse[];
    allowMultipleOpen?: boolean;
    searchable?: boolean;
    /*
     * Present renders the rules affordance; absent is FAQs alone. Typed from
     * the generated model rather than mirrored locally — the generator is the
     * single author of this shape.
     */
    rulesSections?: SurfaceRulesSection[];
    rulesLabel?: string;
    rulesTitle?: string;
    /** One line under the section title, above the legal summary. */
    intro?: string;
}

export interface SurfaceLeaderboardDemoEntry {
    rank: number;
    name: string;
    score: number;
    avatarUrl?: string;
    change?: number;
}

export interface SurfaceLeaderboardBlockConfigResponse {
    competitionId?: string;
    /** Ledger board (LedgerBoard) id — required when source is LEDGER */
    ledgerBoardId?: string;
    source?: "COMPETITION" | "HUB_POINTS" | "DEMO" | "LEDGER";
    maxRows?: number;
    showCurrentUser?: boolean;
    showPlatformIcons?: boolean;
    layout?: "full" | "compact" | "sidebar";
    demoEntries?: SurfaceLeaderboardDemoEntry[];
    /** Pin a "My Position" row at the bottom even when user is outside the top N */
    showMyPosition?: boolean;
}

export type SurfaceCtaAction = "FOLLOW_HUB" | "EXTERNAL_LINK" | "SCROLL_TO_SECTION" | "LOGIN";

export interface SurfaceCtaConfigResponse {
    headline: string;
    subtext?: string;
    buttonText: string;
    buttonAction: SurfaceCtaAction;
    buttonUrl?: string;
    targetSectionId?: string;
}

export interface SurfaceVideoConfigResponse {
    source: "UPLOAD" | "YOUTUBE" | "TWITCH_CLIP";
    videoUrl?: string;
    videoAssetId?: string;
    posterImageId?: string;
    posterImageUrl?: string;
    autoplay?: boolean;
    loop?: boolean;
    maxHeight?: number;
    aspectRatio?: "16:9" | "21:9" | "4:3" | "1:1";
}

export interface SurfaceBlockLayout {
    columns?: 1 | 2 | 3;
    columnSplit?: "50-50" | "60-40" | "70-30" | "40-60" | "30-70";
    stackOnMobile?: boolean;
}

export interface SurfaceStoreLocationResponse {
    _id?: string;
    name: string;
    address?: string;
    city?: string;
    state?: string;
    zipCode?: string;
    lat?: number;
    lng?: number;
    phone?: string;
    hours?: string;
    url?: string;
    category?: string;
}

export interface SurfaceStoreLocatorConfigResponse {
    heading?: string;
    description?: string;
    locations?: SurfaceStoreLocationResponse[];
    searchRadiusMiles?: number;
    defaultView?: "map" | "list";
    maxResults?: number;
}

export interface SurfaceMyPositionStatItemResponse {
    _id?: string;
    label: string;
    valuePrefix?: string;
}

export interface SurfaceMyPositionConfigResponse {
    title?: string;
    profileImageUrl?: string;
    primaryStats?: SurfaceMyPositionStatItemResponse[];
    secondaryStats?: SurfaceMyPositionStatItemResponse[];
    ctaText?: string;
    ctaTargetBlockId?: string;
    dataSource?: "COMPETITION" | "HUB_POINTS" | "LEDGER";
    competitionId?: string;
    /** Ledger board (LedgerBoard) id — required when dataSource is LEDGER */
    ledgerBoardId?: string;
}

export interface SurfaceContentBlockResponse {
    _id: string;
    /** The serializer's id form — `_id` is stripped on some responses, so both are read. */
    id?: string;
    type: SurfaceContentBlockType;
    enabled: boolean;
    order: number;
    /** Nav inclusion. Unset = legacy rule (titled blocks appear); true/false overrides. */
    showInNav?: boolean;
    /** Nav-only label when it differs from the section heading ("Earn"). */
    navLabel?: string;
    /** Nav glyph override; the block type's default glyph when unset. */
    navIcon?: string;
    /** User-assignable stable slug for scroll nav targeting */
    blockId?: string;

    // HTML type
    htmlContent?: string;
    widthPercentage?: number;

    // PROMO type
    promoItems?: SurfacePromoItemResponse[];
    promoLayout?: "2-up" | "3-up";

    // TEXT_IMAGE type
    textContent?: string;
    imageUrl?: string;
    imagePosition?: "left" | "right";

    // REWARDS type
    rewardsConfig?: SurfaceRewardsConfigResponse;

    // PRODUCTS type
    productsConfig?: SurfaceProductsConfigResponse;

    // COUNTDOWN type
    countdownSource?: "AUTO" | "FIXED";
    countdownTargetDate?: number;
    countdownLabel?: string;

    // SPONSORS type
    sponsors?: SurfaceSponsorItemResponse[];

    // LIVE_BRACKET type
    bracketCompetitionId?: string;

    /** Optional section title override (shared across block types) */
    sectionTitle?: string;

    /** Per-block visual design controls */
    blockDesign?: SurfaceBlockDesign;

    // STEPS type
    stepsConfig?: SurfaceStepsConfigResponse;

    // PRIZES type
    prizesConfig?: SurfacePrizesConfigResponse;

    // FAQ type
    faqConfig?: SurfaceFaqConfigResponse;

    // LEADERBOARD type
    leaderboardConfig?: SurfaceLeaderboardBlockConfigResponse;

    // CTA_BANNER type
    ctaConfig?: SurfaceCtaConfigResponse;

    // VIDEO type
    videoConfig?: SurfaceVideoConfigResponse;

    // Layout composition
    layout?: SurfaceBlockLayout;

    // IMAGE_GALLERY type
    imageGalleryConfig?: SurfaceImageGalleryConfigResponse;

    // STATS type
    statsConfig?: SurfaceStatsConfigResponse;

    // SOCIAL_EMBED type
    socialEmbedConfig?: SurfaceSocialEmbedConfigResponse;

    // STREAMING type
    streamingConfig?: SurfaceStreamingBlockConfigResponse;

    // NOTIFICATION_SIGNUP type
    notificationSignupConfig?: SurfaceNotificationSignupConfigResponse;

    // POLL type
    pollConfig?: SurfacePollConfigResponse;

    // CONTAINER type — nested child blocks
    containerConfig?: {
        children?: SurfaceContentBlockResponse[];
    };

    // COLUMNS type — multi-column layout
    columnsConfig?: {
        columnCount?: 2 | 3;
        columnSplit?: string;
        stackOnMobile?: boolean;
        columns?: SurfaceContentBlockResponse[][];
    };

    // SCROLL_NAV type — anchor navigation
    scrollNavConfig?: {
        items?: Array<{ _id: string; label: string; targetBlockId?: string }>;
        sticky?: boolean;
        stickyTopOffset?: number;
        style?: "tabs" | "pills" | "minimal";
        alignment?: "left" | "center" | "right";
    };

    // STORE_LOCATOR type — store/retailer finder
    storeLocatorConfig?: SurfaceStoreLocatorConfigResponse;

    // MY_POSITION type — user rank/score/stats widget
    myPositionConfig?: SurfaceMyPositionConfigResponse;

    // ONBOARDING type — dismissible step overlay
    onboardingConfig?: {
        title?: string;
        steps: Array<{ title: string; description: string; imageUrl?: string }>;
        dismissible?: boolean;
    };


    // CODE_ENTRY type — code redemption CTA
    codeEntryConfig?: {
        headline?: string;
        description?: string;
        buttonText?: string;
        questConfigId?: string;
        milestoneId?: string;
    };

    // GAMIFICATION type — renders a GamificationModel
    gamificationConfig?: {
        gamificationModelId: string;
        displayMode?: string;
        /** Denormalized from the referenced GamificationModel on read (`resolveGamificationTypes`). */
        gamificationType?: string;
    };

    // Entity reference blocks (PLAYABLE_AD, BRAND_CHALLENGE, QUEST_SPONSORSHIP, GUILD_SPONSORSHIP)
    activationId?: string;

    // COMPETITION block
    competitionId?: string;

    // QUEST block
    questConfigId?: string;

    // LOOTBOX block
    lootBoxConfigId?: string;

    // CHALLENGE_ZONE block
    challengeZoneConfig?: {
        variant: "SCAVENGER_HUNT" | "LEADERBOARD_MINI" | "BINGO_MINI";
        brandLogoUrl?: string;
        brandName?: string;
        challengeType?: string;
        countdownEndAt?: number;
        rewardIcon?: string;
        rewardLabel?: string;
        rewardDescription?: string;
        ctaText?: string;
        ctaUrl?: string;
        moreActivations?: Array<{ logoUrl?: string; brandName: string; type: string }>;
        objectives?: Array<{ label: string; sublabel?: string; tag?: "BRAND" | "GAME" | "REWARD"; state: "unchecked" | "partial" | "done" }>;
        leaderboardEntries?: Array<{ rank: number; avatarUrl?: string; name: string; score: number; isCurrentUser?: boolean }>;
        bingoCells?: Array<{ label: string; state: "unchecked" | "done" | "free" }>;
        bingoPerLineReward?: string;
        gamificationModelId?: string;
    };

    // Phase-based visibility gate
    visiblePhase?: "always" | "pre_launch" | "live";

    // STREAM_ZONE block
    streamZoneConfig?: {
        channelUrl?: string;
        placeholderImageUrl?: string;
        showChat?: boolean;
        hubBadgeEnabled?: boolean;
        hubBadgeMemberCount?: string;
        activityNotificationEnabled?: boolean;
        activityNotificationText?: string;
        challengeProgressEnabled?: boolean;
        challengeProgressLabel?: string;
        challengeProgressName?: string;
        challengeProgressValue?: number;
        challengeProgressStat?: string;
        sponsorPillsEnabled?: boolean;
        sponsorPillSponsors?: Array<{ iconUrl?: string; name: string }>;
        streamTitle?: string;
        chatButtonUrl?: string;
        earnItems?: Array<{ icon?: string; label: string; points: number; active?: boolean }>;
        tickerItems?: Array<{ text: string; color?: "orange" | "green" | "red" }>;
        tickerSpeed?: number;
    };

    // Display mode for entity blocks
    displayMode?: string;

    // Multi-page support
    pageIndex?: number;

    // Block scheduling (epoch ms)
    showAfter?: number;
    showUntil?: number;
}

/*
 * ============================================
 * Social Profile Types
 * ============================================
 */

export type SocialPlatform =
    | "TWITCH"
    | "YOUTUBE"
    | "KICK"
    | "TWITTER"
    | "TIKTOK"
    | "INSTAGRAM"
    | "DISCORD"
    | "FACEBOOK"
    | "STREAMLABS"
    | "TWITCH_TEAM"
    | "OTHER";

export interface SocialProfileResponse {
    platform: SocialPlatform;
    platformUserId: string;
    username: string;
    displayName?: string;
    profileUrl?: string;
    profileImageUrl?: string;
    bannerImageUrl?: string;
    followers?: number;
    gamesPlayed?: string[];
}

export type HubType = "BRAND" | "CREATOR" | "COMMUNITY" | "COMPETITION" | "GENERAL";
export type SurfaceVisibility = "PUBLIC" | "PRIVATE";
export type HubFollowMode = "OPEN" | "APPROVAL" | "INVITE_ONLY";

export type SurfaceThemePreset = "DARK_GAMING" | "BRAND_MINIMAL" | "NEON_CYBERPUNK" | "MILITARY_TACTICAL" | "LIGHT_CLEAN";

export interface HubResponse {
    id: string;
    slug: string;
    name: string;
    description?: string;
    hubType?: HubType;
    visibility?: SurfaceVisibility;
    followMode?: HubFollowMode;
    followerCount?: number;
    brand?: {
        id: string;
        name: string;
        brandColor?: string;
    };
    status: "DRAFT" | "ACTIVE" | "ARCHIVED";

    // Creator Profile Images (auto-populated from socials)
    creatorProfileImageUrl?: string;
    creatorHeaderImageUrl?: string;

    // Per-hub game allowlist
    allowedGames?: Array<{
        gameId: string;
        enabledActivityTypes: {
            leaderboards: boolean;
            challenges: boolean;
            tournaments: boolean;
        };
    }>;

    // Challenge Page Design System (shared foundation)
    themeColors?: SurfaceThemeColors;
    typography?: SurfaceTypography;
    themeConfig?: SurfaceThemeConfigResponse;
    heroSection?: HubHeroSection;
    navbarConfig?: HubNavbarConfig;
    seo?: HubSeo;

    // Brand assets
    brandLogoId?: string;
    brandLogoUrl?: string;

    // Dynamic Content
    competitionId?: string;
    questConfigs: string[];

    // Derived games (computed from competitions + social profiles)
    derivedGames?: DerivedGameResponse[];

    // Point system configuration
    pointSystemConfig?: SurfacePointSystemConfigResponse;

    // Hub-Specific Components
    sections: HubSection[];
    retailCampaigns: HubRetailCampaign[];
    streamingSettings?: StreamingSettings;
    streamingTeamId?: string;

    // Dynamic content blocks
    contentBlocks?: SurfaceContentBlockResponse[];

    /** Hub lifecycle phase — controls visiblePhase filtering on content blocks */
    phase?: "pre_launch" | "live";

    /** Puck visual builder data (read-only — used for page archetype info) */
    pageBuilderData?: {
        root?: {
            props?: {
                pageArchetype?: "single-page" | "multi-page";
                pages?: Array<{ label: string }>;
            };
        };
    };

    // Social connections (parsed from URLs)
    socials?: SocialProfileResponse[];

    createdAt: number;
    updatedAt: number;
}

/**
 * Fetches hub config by slug. Supports auth (Bearer token) and preview mode
 * (previewToken query param — signed Redis-backed token for non-admin previews).
 * Both `preview=true` (admin) and `previewToken=<token>` (signed link) flow
 * through the same generated client method.
 */
async function fetchHub(slug: string, fullToken?: Token, previewToken?: string): Promise<HubResponse> {
    /*
     * Reads a DROP. Hub is retired — `/hubs/slug/:slug` is gone.
     *
     * The two campaign pages that still mount this (RTTR, Full Throttle) read
     * exactly one field off it, `name`, as image alt text with literal
     * fallbacks; everything else they render comes from `customHubs/registry.ts`
     * and the Challenge model. So a Drop answers this completely.
     */
    const data = await new DropsApi(getConfig(fullToken)).getDropsSlugSlug({slug, preview: previewToken ? "true" : undefined});
    return data as unknown as HubResponse;
}

/**
 * Get preview token from URL query params
 */
function getPreviewTokenFromUrl(): string | undefined {
    if (typeof window === "undefined") return undefined;
    const params = new URLSearchParams(window.location.search);
    return params.get("previewToken") || undefined;
}

/**
 * @param enabled pass `false` to skip the fetch entirely. A registered campaign
 *   (`customHubs/registry.ts`) renders from its registration, not from a record —
 *   and neither campaign slug exists in `drops`, so the request can only 404.
 *   Firing it anyway costs a round trip per page load and dispatches a spurious
 *   error into the store on the two most brand-sensitive pages we serve.
 *   Routing is untouched: this changes whether a fetch happens, never any URL.
 */
export function useGetHub(slug?: string, enabled: boolean = true) {
    const fullToken = useSelector((store: IStore) => store.authStore.fullToken);
    const previewToken = getPreviewTokenFromUrl();

    return useQuery({
        queryKey: ["hub", slug, fullToken?.id ?? "unauthenticated", previewToken ?? "public"],
        queryFn: () => fetchHub(slug!, fullToken, previewToken),
        staleTime: 30 * 60000, // 30 minutes
        enabled: enabled && Boolean(slug),
        placeholderData: keepPreviousData,
    });
}

/**
 * Get enabled sections sorted by order
 */
export function getEnabledSections(hub: HubResponse): HubSection[] {
    return (hub.sections || [])
        .filter(s => s.enabled)
        .sort((a, b) => a.order - b.order);
}

/**
 * Get enabled retail campaigns sorted by order
 */
export function getEnabledRetailCampaigns(hub: HubResponse): HubRetailCampaign[] {
    return (hub.retailCampaigns || [])
        .filter(c => c.enabled)
        .sort((a, b) => a.order - b.order);
}

